import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Camera, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { generateImagePreview, cleanupImagePreviews } from '@/services/imageUploadService';

interface StagedImage {
  id: string;
  file?: File;
  url: string;
  isExisting: boolean;
}

interface ReviewImageUploadProps {
  images: File[];
  existingImages?: string[];
  onImagesChange: (images: File[]) => void;
  onExistingImagesChange?: (images: string[]) => void;
  onImageMarkedForDeletion?: (imageUrl: string) => void;
  maxImages?: number;
  disabled?: boolean;
}

const ReviewImageUpload: React.FC<ReviewImageUploadProps> = ({
  images,
  existingImages = [],
  onImagesChange,
  onExistingImagesChange,
  onImageMarkedForDeletion,
  maxImages = 5,
  disabled = false,
}) => {
  const [stagedImages, setStagedImages] = useState<StagedImage[]>(() => {
    const existingStaged = existingImages.map((url, index) => ({
      id: `existing-${index}`,
      url,
      isExisting: true
    }));
    const newStaged = images.map((file, index) => ({
      id: `new-${index}`,
      file,
      url: generateImagePreview(file),
      isExisting: false
    }));
    return [...existingStaged, ...newStaged];
  });
  
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync with external changes
  React.useEffect(() => {
    const existingStaged = existingImages.map((url, index) => ({
      id: `existing-${index}`,
      url,
      isExisting: true
    }));
    const newStaged = images.map((file, index) => ({
      id: `new-${index}`,
      file,
      url: generateImagePreview(file),
      isExisting: false
    }));
    setStagedImages([...existingStaged, ...newStaged]);
  }, [existingImages, images]);

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload JPEG, PNG, or WebP images.';
    }

    if (file.size > maxSize) {
      return 'File too large. Maximum size is 5MB.';
    }

    return null;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || disabled) return;

    setError(null);
    const fileArray = Array.from(files);
    
    // Check if adding these files would exceed the limit
    if (stagedImages.length + fileArray.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed. You can upload ${maxImages - stagedImages.length} more.`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      validFiles.push(file);
    }

    // Create new staged images
    const newStagedImages: StagedImage[] = validFiles.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file,
      url: generateImagePreview(file),
      isExisting: false
    }));

    // Update staged images
    setStagedImages(prev => [...prev, ...newStagedImages]);
    
    // Update parent with new file list
    const currentFiles = stagedImages.filter(img => !img.isExisting && img.file).map(img => img.file!);
    const allNewFiles = [...currentFiles, ...validFiles];
    onImagesChange(allNewFiles);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (imageId: string) => {
    if (disabled) return;
    
    setStagedImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      const newImages = prev.filter(img => img.id !== imageId);
      
      // If removing an existing Firebase Storage image, mark it for deletion
      if (imageToRemove?.isExisting && imageToRemove.url.includes('firebasestorage.googleapis.com') && onImageMarkedForDeletion) {
        onImageMarkedForDeletion(imageToRemove.url);
      }
      
      // Update parent with new file list and existing images list
      const newFiles = newImages.filter(img => !img.isExisting && img.file).map(img => img.file!);
      const newExistingImages = newImages.filter(img => img.isExisting).map(img => img.url);
      
      onImagesChange(newFiles);
      if (onExistingImagesChange) {
        onExistingImagesChange(newExistingImages);
      }
      
      return newImages;
    });
    
    setError(null);
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Images (Optional)
        </label>
        <span className="text-xs text-gray-500">
          {stagedImages.length}/{maxImages} images
        </span>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Image Preview Grid */}
      {stagedImages.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {stagedImages.map((image, index) => (
            <div key={image.id} className="relative group">
              <img
                src={image.url}
                alt={`Review image ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-gray-200"
              />
              {/* Existing image indicator */}
              {image.isExisting && (
                <div className="absolute top-1 left-1">
                  <span className="bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded">
                    Existing
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeImage(image.id)}
                disabled={disabled}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {stagedImages.length < maxImages && (
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${dragActive 
              ? 'border-jamaica-green bg-jamaica-green/5' 
              : 'border-gray-300 hover:border-jamaica-green hover:bg-jamaica-green/5'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
          />
          
          <div className="flex flex-col items-center space-y-2">
            <div className="flex items-center space-x-2">
              <Camera className="h-8 w-8 text-gray-400" />
              <Upload className="h-6 w-6 text-gray-400" />
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium text-jamaica-green">Click to upload</span> or drag and drop
            </div>
            <div className="text-xs text-gray-500">
              PNG, JPG, WebP up to 5MB each
            </div>
          </div>
        </div>
      )}

      {/* Upload Button (Alternative) */}
      {stagedImages.length < maxImages && (
        <Button
          type="button"
          variant="outline"
          onClick={openFileDialog}
          disabled={disabled}
          className="w-full border-jamaica-green text-jamaica-green hover:bg-jamaica-green/10"
        >
          <Camera className="h-4 w-4 mr-2" />
          Add Images ({stagedImages.length}/{maxImages})
        </Button>
      )}

      <p className="text-xs text-gray-500">
        Add images to help other customers see this product in use. Images will be reviewed before being published.
      </p>
    </div>
  );
};

export default ReviewImageUpload;